'use client';

import React from 'react';
import { toast } from 'sonner';
import { Zap, CheckCircle2, Download, CreditCard } from 'lucide-react';
import Badge from '@/components/ui/Badge';

const invoices = [
  { id: 'inv-001', date: 'Jul 1, 2026', amount: '$149.00', status: 'paid', description: 'Growth Plan — July 2026' },
  { id: 'inv-002', date: 'Jun 1, 2026', amount: '$149.00', status: 'paid', description: 'Growth Plan — June 2026' },
  { id: 'inv-003', date: 'May 1, 2026', amount: '$149.00', status: 'paid', description: 'Growth Plan — May 2026' },
  { id: 'inv-004', date: 'Apr 1, 2026', amount: '$99.00', status: 'paid', description: 'Starter Plan — April 2026' },
  { id: 'inv-005', date: 'Mar 1, 2026', amount: '$99.00', status: 'paid', description: 'Starter Plan — March 2026' },
];

const planFeatures = [
  '50,000 emails/day',
  'Unlimited campaigns',
  'Advanced analytics',
  '5 team members',
  'Custom sender domains',
  'Priority support',
];

export default function BillingSettings() {
  return (
    <div className="space-y-6">
      {/* Current plan */}
      <div className="card p-6 border-primary/20 bg-primary/5 space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Zap size={18} className="text-primary" />
              <h2 className="text-lg font-bold text-foreground">Growth Plan</h2>
              <Badge variant="primary">Current</Badge>
            </div>
            <p className="text-3xl font-bold tabular-nums text-foreground">$149<span className="text-base font-normal text-muted-foreground">/month</span></p>
            <p className="text-sm text-muted-foreground mt-1">Renews August 1, 2026</p>
          </div>
          <button
            onClick={() => toast?.info('Plan management — billing portal integration point')}
            className="btn-secondary"
          >
            Change Plan
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {planFeatures?.map((f) => (
            <div key={`feat-${f}`} className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-green-600 flex-shrink-0" />
              <span className="text-sm text-foreground">{f}</span>
            </div>
          ))}
        </div>

        {/* Usage bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-foreground">Monthly Send Usage</span>
            <span className="tabular-nums text-muted-foreground">1,284,320 / 1,550,000</span>
          </div>
          <div className="w-full h-2.5 bg-border rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: '82.9%' }}
            />
          </div>
          <p className="text-xs text-muted-foreground">82.9% used — 265,680 emails remaining this month</p>
        </div>
      </div>
      {/* Payment method */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="section-header">Payment Method</h2>
          <button
            onClick={() => toast?.info('Update payment method — Stripe integration point')}
            className="btn-secondary text-sm"
          >
            Update Card
          </button>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
          <div className="w-10 h-7 rounded bg-blue-600 flex items-center justify-center">
            <CreditCard size={14} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Visa ending in 4242</p>
            <p className="text-xs text-muted-foreground">Expires 09/2028</p>
          </div>
          <Badge variant="success" className="ml-auto">Active</Badge>
        </div>
      </div>
      {/* Invoice history */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="section-header">Invoice History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                {['Date', 'Description', 'Amount', 'Status', '']?.map((col) => (
                  <th key={`inv-th-${col}`} className="px-5 py-3 text-left metric-label">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices?.map((inv) => (
                <tr key={inv?.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="px-5 py-3 text-sm text-muted-foreground whitespace-nowrap">{inv?.date}</td>
                  <td className="px-5 py-3 text-sm font-medium text-foreground">{inv?.description}</td>
                  <td className="px-5 py-3 text-sm tabular-nums font-semibold text-foreground">{inv?.amount}</td>
                  <td className="px-5 py-3">
                    <Badge variant="success">Paid</Badge>
                  </td>
                  <td className="px-5 py-3">
                    <button
                      onClick={() => toast?.info(`Downloading ${inv?.id}.pdf`)}
                      title="Download invoice PDF"
                      className="w-7 h-7 rounded flex items-center justify-center hover:bg-muted transition-all"
                    >
                      <Download size={13} className="text-muted-foreground" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}